import React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToastDefault } from "@/hooks/use-toast-default";
import { useReactHookFormAxiosValidation } from "@/hooks/use-react-hook-form-axios-validation";
import { FormInput } from "@/components/forms/input";
import { FormSelect } from "@/components/ui/select";
import { useGetSnapshotCollectionsQuery } from "../../snapshotCollection.api";
import { BusinessReport, BusinessReportPrompt } from "@/modules/snapshots/businessReports.types";
import { Item } from "@/types/fse";
import { useCreateBusinessReportOutcomeMutation } from "../../businessReports.api";
import * as analytics from "@/services/analytics";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import {
  BATCH_SIZE,
  CREATE_NEW_COLLECTION_KEY,
} from "@/modules/snapshots/modals/generateSnapshotCollectionModal/consts";
import { GenerateSnapshotCollectionFormInputs } from "@/modules/snapshots/modals/generateSnapshotCollectionModal/types";
import {
  getSnapshotCollectionOptions,
  validateSnapshotCollection,
} from "@/modules/snapshots/modals/generateSnapshotCollectionModal/helpers";

interface Props {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  businessReport: BusinessReport;
  fsEntries: Item[];
  variables?: Record<string, string>;
  prompts: BusinessReportPrompt[];
}

export const GenerateSnapshotCollectionModal: React.FC<Props> = ({
  prompts,
  variables,
  isOpen,
  setIsOpen,
  fsEntries,
  businessReport,
}) => {
  const { showToast } = useToastDefault();
  const [createBusinessReportOutcome] = useCreateBusinessReportOutcomeMutation();
  const navigate = useNavigate();
  const isBatch = fsEntries.length > 1;

  const methods = useForm<GenerateSnapshotCollectionFormInputs>({
    defaultValues: {
      document_name: fsEntries.length === 1 ? fsEntries[0].name : "",
      name: fsEntries.length === 1 ? fsEntries[0].name : "",
    },
  });

  const { data: snapshotCollections = [] } = useGetSnapshotCollectionsQuery({
    business_report_id: businessReport.id,
  });

  const handleReactHookFormAxiosValidation = useReactHookFormAxiosValidation<GenerateSnapshotCollectionFormInputs>();

  const snapshotCollectionsOptions = React.useMemo(
    () => getSnapshotCollectionOptions(snapshotCollections),
    [snapshotCollections],
  );

  const selectedSnapshotCollection = useWatch({
    control: methods.control,
    name: "snapshot_collection_id",
  });

  React.useEffect(() => {
    if (selectedSnapshotCollection !== CREATE_NEW_COLLECTION_KEY) {
      methods.setValue("snapshot_collection_new", "");
    }
  }, [methods, selectedSnapshotCollection]);

  const onSubmit = async (payload: GenerateSnapshotCollectionFormInputs) => {
    if (!validateSnapshotCollection(selectedSnapshotCollection, payload, methods.setError)) {
      return;
    }

    try {
      let snapshotCollectionId = null;
      let result;

      for (let i = 0; i < fsEntries.length; i += BATCH_SIZE) {
        const batched = fsEntries.slice(i, i + BATCH_SIZE);
        try {
          result = await createBusinessReportOutcome({
            fs_entries: batched.map(entry => entry.id),
            business_report_id: businessReport.id,
            business_report_prompts: prompts.map(prompt => ({
              order: prompt.order,
              business_report_prompt_id: prompt.id,
              prompt: prompt.prompt,
              name: prompt.name,
            })),
            snapshot_collection_id: snapshotCollectionId
              ? snapshotCollectionId
              : payload.snapshot_collection_id === CREATE_NEW_COLLECTION_KEY
                ? undefined
                : payload.snapshot_collection_id,
            snapshot_collection_new: snapshotCollectionId ? undefined : payload.snapshot_collection_new,
            variables: variables || {},
            name: payload.name,
          }).unwrap();
        } catch (error) {
          toast.error("Error while saving");
          Sentry.captureException(error);
          return;
        }

        if (!snapshotCollectionId) {
          snapshotCollectionId = result.snapshot_collection_id;
        }
      }

      setIsOpen(false);
      showToast("Success", "SnapShot collection updated");
      analytics.snapshotCreate(businessReport.name);

      navigate(
        `/snapshots/collections?businessReportId=${businessReport.id}&snapshotCollectionId=${snapshotCollectionId}&refresh=true`,
      );
    } catch (error) {
      handleReactHookFormAxiosValidation(error, methods.setError);
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <form method="post" onSubmit={methods.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>Saving Snapshot to Collection</DialogTitle>
            </DialogHeader>
            {!isBatch && (
              <>
                <FormInput
                  autoFocus={false}
                  name="document_name"
                  readOnly={true}
                  placeholder="Document name"
                  className="my-3"
                />

                <FormInput autoFocus={false} name="name" placeholder="SnapShot name" className="my-3" />
              </>
            )}

            <div className="my-3">
              <FormSelect
                name="snapshot_collection_id"
                placeholder="Select snapshot collection"
                options={snapshotCollectionsOptions}
                required={true}
              />
            </div>

            {selectedSnapshotCollection === CREATE_NEW_COLLECTION_KEY && (
              <FormInput name="snapshot_collection_new" placeholder="New snapshot collection name" className="my-3" />
            )}

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={methods.formState.isSubmitting}>
                {methods.formState.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
};
